import {
  Injectable,
  Renderer2,
  RendererFactory2,
  SecurityContext,
} from '@angular/core';
import * as L from 'leaflet';

import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { LatLngBounds, Map } from 'leaflet';
import { PoiChartData } from './grid-widget-config.service';

interface GestureHandlingMapOptions extends L.MapOptions {
  gestureHandling?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class LeafletMapboxService {
  private readonly accessToken =
    'pk.eyJ1IjoiY2hlZ25lciIsImEiOiJjbDN5NWZpcmUwZTkyM2NuNXQ3NGJwOGExIn0.R-Cl0ThFwUS6xI1S61rEtQ';

  private renderer: Renderer2;
  private tileLayerCache: { [key: string]: L.TileLayer } = {};
  private styleCache: globalThis.Map<string, object> = new globalThis.Map();
  private styleFetchPromises: globalThis.Map<string, Promise<object>> =
    new globalThis.Map();

  private tiles: { [key: string]: string } = {
    //dark: 'assets/vector-layers/dark.json',
    light: 'assets/vector-layers/light.json',
    traffic: 'assets/vector-layers/traffic.json',
    streets: 'assets/vector-layers/streets.json',
    satellite: 'assets/vector-layers/satellite.json',
  };

  constructor(
    private rendererFactory: RendererFactory2,
    private sanitizer: DomSanitizer
  ) {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  initializeMap(mapContainer: HTMLElement): Map {
    const mapOptions: GestureHandlingMapOptions = {
      doubleClickZoom: true,
      boxZoom: true,
      touchZoom: true,
      scrollWheelZoom: false,
      gestureHandling: true,
      zoomControl: false, // Disable the default zoom control
      zoomSnap: 0.25,
    };

    const map = new Map(mapContainer, mapOptions);

    // Ensure the default panes exist (Leaflet creates them on map init)
    // tilePane: 200
    // overlayPane: 400
    // markerPane: 600

    // Create a custom pane for isochrones to ensure they are always on top of other polygons
    // We want them above overlayPane (400) but below markerPane (600)
    const isochronePane = map.createPane('isochronePane');
    if (isochronePane) {
      isochronePane.style.zIndex = '450';
    }

    return map;
  }

  /**
   * Creates and returns a Mapbox GL layer of the specified type.
   *
   * @param {string} type - The type of Mapbox GL layer to create.
   * @return {L.Layer} The created Mapbox GL layer.
   */
  createMapboxGLLayer(type: string): L.Layer {
    if (!this.tileLayerCache[type]) {
      this.initMapboxGLLayer(type);
    }

    return this.tileLayerCache[type];
  }

  /**
   * Initializes a Mapbox GL layer of the specified type and caches it.
   *
   * @param {string} type - The type of the layer to initialize. This corresponds to a key in the `tiles` object.
   * @return {void} This function does not return any value.
   */
  initMapboxGLLayer(type: string): void {
    this.tileLayerCache[type] = this.createMapboxGLLayerInstance(type);
  }

  /**
   * Creates a new Mapbox GL layer instance for the given type.
   * Uses the cached style object if available, otherwise falls back to the style URL.
   *
   * @param {string} type - The layer type key.
   * @return {L.TileLayer} The created layer instance.
   */
  private createMapboxGLLayerInstance(type: string): L.TileLayer {
    const cachedStyle = this.styleCache.get(type);
    return (L as any).mapboxGL({
      accessToken: this.accessToken,
      style: cachedStyle ?? this.tiles[type],
      preserveDrawingBuffer: true,
      attribution:
        '© <a href="https://www.mapbox.com/about/maps/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a> <strong><a href="https://www.mapbox.com/map-feedback/" target="_blank">Improve this map</a></strong>',
    });
  }

  /**
   * Creates a fresh Mapbox GL layer instance for the given type.
   * Each call returns a new instance (required since a Leaflet layer can only
   * be added to one map at a time), but reuses the cached style object to
   * avoid redundant network requests.
   *
   * @param {string} type - The layer type key.
   * @return {L.Layer} A new layer instance using the cached or URL-based style.
   */
  createFreshMapboxGLLayer(type: string): L.Layer {
    return this.createMapboxGLLayerInstance(type);
  }

  /**
   * Pre-fetches and caches the style JSON for the given layer type.
   * Subsequent calls for the same type return the cached promise.
   *
   * @param {string} type - The layer type key.
   * @return {Promise<object>} Resolves with the parsed style object.
   */
  prefetchStyle(type: string): Promise<object> {
    if (this.styleCache.has(type)) {
      return Promise.resolve(this.styleCache.get(type)!);
    }
    if (this.styleFetchPromises.has(type)) {
      return this.styleFetchPromises.get(type)!;
    }
    const url = this.tiles[type];
    if (!url) return Promise.reject(`Unknown tile type: ${type}`);

    const promise = fetch(url)
      .then(r => r.json())
      .then((styleObj: object) => {
        this.styleCache.set(type, styleObj);
        this.styleFetchPromises.delete(type);
        return styleObj;
      });
    this.styleFetchPromises.set(type, promise);
    return promise;
  }

  /**
   * Pre-fetches all known tile styles into the in-memory cache.
   * Call this once at app startup to warm the cache before any map initializes.
   *
   * @return {void}
   */
  prefetchAllStyles(): void {
    Object.keys(this.tiles).forEach(type => this.prefetchStyle(type));
  }

  /**
   * Remove all layers from the map except the specified one.
   * @param map Leaflet map instance.
   * @param excludeLayer Layer to keep on the map.
   * @param keepIsochrones If true, layers with an ID starting with 'isochrone-' will not be removed.
   */
  clearLayers(
    map: L.Map,
    excludeLayer?: L.Layer,
    keepIsochrones = false
  ): void {
    map.eachLayer(layer => {
      if (excludeLayer && layer === excludeLayer) return;

      if (keepIsochrones) {
        const identifiableLayer = layer as any;
        if (
          identifiableLayer.id &&
          identifiableLayer.id.startsWith('isochrone-')
        ) {
          return;
        }
      }

      map.removeLayer(layer);
    });
  }

  getMapBoxToken(): string {
    return this.accessToken;
  }

  /**
   * Creates a Leaflet GeoJSON layer for isochrone data.
   *
   * @param {any} geoJsonData - The GeoJSON data from Mapbox Isochrone API.
   * @param {string[]} colors - Optional array of colors for the isochrone intervals.
   * @param {any} options - Optional Leaflet GeoJSON options.
   * @param {number[]} minutes - Optional array of minutes requested, used to match colors to contours.
   * @return {L.GeoJSON} The created GeoJSON layer.
   */
  createIsochroneLayer(
    geoJsonData: any,
    colors: string[] = [],
    options: any = {},
    minutes: number[] = []
  ): L.GeoJSON {
    // If we have features, we can map them by index to colors
    const features = geoJsonData.features || [];

    const defaultStyle = (feature: any) => {
      // Mapbox API provides 'fill' and 'fill-opacity' in properties
      // If colors are provided, we use them based on the feature's index
      // Mapbox returns features in order of intervals.
      // We can try to find the index of this feature in the original data
      let index = feature.properties?.index;

      if (index === undefined) {
        // Try to match by contour value if minutes are provided
        const contour = feature.properties?.contour;
        if (contour !== undefined && minutes && minutes.length > 0) {
          index = minutes.indexOf(contour);
        }

        if (index === undefined || index === -1) {
          // Fallback: search by contour value or just use order if we can
          index = features.findIndex(
            (f: any) =>
              f === feature ||
              (f.properties?.contour === feature.properties?.contour &&
                f.geometry.coordinates === feature.geometry.coordinates)
          );
        }
        if (index === -1 || index === undefined) index = 0;
      }

      let fillColor = feature.properties?.fill || '#5a3fc0';

      if (colors.length > 0) {
        // Use colors based on index.
        // If we matched via minutes, index is the correct position in the colors array.
        fillColor = colors[index % colors.length];
      }

      return {
        fillColor: fillColor,
        fillOpacity: feature.properties?.['fill-opacity'] || 0.3,
        color: fillColor,
        weight: 1,
        opacity: 0.8,
        ...options.style?.(feature),
      };
    };

    return L.geoJSON(geoJsonData, {
      pane: 'isochronePane',
      ...options,
      style: defaultStyle,
    });
  }

  createMarker(
    latlng: L.LatLngExpression,
    svgCode: SafeHtml | undefined,
    classColor: string,
    iconColor: string,
    size = 1
  ): L.Marker {
    const marker = L.marker(latlng);
    let html = '';
    if (svgCode) {
      const svg = this.sanitizer.sanitize(SecurityContext.HTML, svgCode) || '';
      html = `<div class="" style="transform: scale(${size});"><div class="poi-icon" style="background-color: ${classColor}; fill:${iconColor};">${svg}</div></div>`;
    }

    const divIcon = L.divIcon({
      className: '',
      html: html,
      iconSize: [40 * size, 40 * size],
      iconAnchor: [20 * size, 40 * size],
    });

    // Update the marker's icon
    marker.setIcon(divIcon);

    return marker;
  }

  pieChartSvgFromData(chartData: PoiChartData[], chartSize: number): string {
    // Create SVG ring chart
    const total = chartData.reduce((sum, d) => sum + Math.abs(d.value), 0);
    let currentAngle = -90; // Start from top

    const radius = chartSize / 2;
    const innerRadius = radius * 0.55; // 55% inner radius for ring effect
    const centerX = radius;
    const centerY = radius;

    let pathsHTML = '';
    chartData.forEach(segment => {
      const percentage = Math.abs(segment.value) / total;
      const angleSize = percentage * 360;

      const startAngle = currentAngle;
      const endAngle = currentAngle + angleSize;

      const startRad = (startAngle * Math.PI) / 180;
      const endRad = (endAngle * Math.PI) / 180;

      const x1 = centerX + radius * Math.cos(startRad);
      const y1 = centerY + radius * Math.sin(startRad);
      const x2 = centerX + radius * Math.cos(endRad);
      const y2 = centerY + radius * Math.sin(endRad);

      const x3 = centerX + innerRadius * Math.cos(endRad);
      const y3 = centerY + innerRadius * Math.sin(endRad);
      const x4 = centerX + innerRadius * Math.cos(startRad);
      const y4 = centerY + innerRadius * Math.sin(startRad);

      const largeArc = angleSize > 180 ? 1 : 0;

      pathsHTML += `
            <path d="M ${x1} ${y1}
                     A ${radius} ${radius} 0 ${largeArc} 1 ${x2} ${y2}
                     L ${x3} ${y3}
                     A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${x4} ${y4}
                     Z"
                  fill="${segment.color}"
                  stroke="white"
                  stroke-width="0"/>
          `;

      currentAngle = endAngle;
    });

    return pathsHTML;
  }

  getSanitizedSvg(svgCode: SafeHtml): SafeHtml {
    return this.sanitizer.sanitize(SecurityContext.HTML, svgCode) || '';
  }

  getBoundsFromObject(bounds: any): LatLngBounds {
    const sw = bounds._southWest;
    const ne = bounds._northEast;
    return L.latLngBounds(L.latLng(sw.lat, sw.lng), L.latLng(ne.lat, ne.lng));
  }
}
